import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { buildApiUrl } from '../utils/api';
// @ts-expect-error shared auth ide error
import sharedAuth from '../services/auth.js';

interface ChillSetterData {
  id: string;
  username: string;
  email: string;
  affiliateCode: string;
  referralLink: string;
  totalReferrals: number;
  totalEarnings: number;
  isActive: boolean;
  createdAt: string;
}

interface CoolKidData {
  _id: string;
  userData: {
    name: string;
    email: string;
    walletAddress: string;
    ensName?: string;
  };
  walletDisplay?: string;
  status: string;
  commission: number;
  createdAt: string;
}

interface ChillSetterStats {
  totalReferrals: number;
  pendingReferrals: number;
  completedReferrals: number;
  totalEarnings: number;
  activeReferrals: number;
}

const ChillSetterDashboard: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [chillSetterData, setChillSetterData] = useState<ChillSetterData | null>(null);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [coolKids, setCoolKids] = useState<CoolKidData[]>([]);
  const [stats, setStats] = useState<ChillSetterStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', text: 'Link copied to clipboard!' });
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage({ type: 'error', text: 'Failed to copy link' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = sharedAuth.getToken();
      if (token) {
        try {
          const result = await sharedAuth.verifyToken();
          if (result.success && result.user) {
            if (result.user.role === 'affiliate' || result.user.role === 'chill_setter') {
              setChillSetterData(result.user);
              setIsLoggedIn(true);
              await fetchChillSetterData(token);
            } else {
              sharedAuth.logout();
              setIsLoggedIn(false);
            }
          } else {
            sharedAuth.logout();
            setIsLoggedIn(false);
          }
        } catch {
          sharedAuth.logout();
          setIsLoggedIn(false);
        }
      }
    };

    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchChillSetterData = async (token: string) => {
    try {
      const apiUrl = buildApiUrl('/api/affiliate/profile');
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        setChillSetterData(result.data);
        await fetchCoolKids(token);
        await fetchStats(token);
      } else {
        sharedAuth.logout();
        setIsLoggedIn(false);
      }
    } catch (error) {
      console.error('Error fetching Chill Setter data:', error);
      setMessage({ type: 'error', text: 'Failed to load dashboard data' });
    }
  };

  const fetchCoolKids = async (token: string) => {
    try {
      const apiUrl = buildApiUrl('/api/affiliate/referrals');
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        setCoolKids(result.data.referrals || []);
      }
    } catch (error) {
      console.error('Error fetching Cool Kids:', error);
    }
  };

  const fetchStats = async (token: string) => {
    try {
      const apiUrl = buildApiUrl('/api/affiliate/stats');
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      const result = await sharedAuth.login(loginData.username, loginData.password);

      if (result.success) {
        setMessage({ type: 'success', text: 'Login successful!' });
        setIsLoggedIn(true);
        setChillSetterData(result.user);
        await fetchChillSetterData(sharedAuth.getToken()!);
        setLoginData({ username: '', password: '' });
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch {
      setMessage({ type: 'error', text: 'Login failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sharedAuth.logout();
    setIsLoggedIn(false);
    setChillSetterData(null);
    setCoolKids([]);
    setStats(null);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-frost-white flex items-center justify-center">
        <div className="bg-white rounded-3xl p-8 md:p-12 border border-ice-teal-light shadow-xl max-w-md w-full mx-4">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-title font-bold text-deep-navy mb-2">CHILL SETTER</h1>
            <p className="text-brand-blue font-body">Access Your Cultural Curator Dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-deep-navy font-bold mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                required
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 placeholder-gray-500 focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue transition-colors"
                placeholder="Enter username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-deep-navy font-bold mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-800 placeholder-gray-500 focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue transition-colors"
                placeholder="Enter password"
              />
            </div>

            {message && (
              <div className={`rounded-xl p-4 ${
                message.type === 'success'
                  ? 'bg-ice-teal/20 border border-ice-teal text-brand-blue'
                  : 'bg-red-50 border border-red-200 text-brand-red'
              }`}>
                {message.text}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Logging in...' : 'ACCESS DASHBOARD'}
            </button>
          </form>

          <div className="text-center mt-6">
            <Link to="/" className="text-gray-500 hover:text-brand-blue transition-colors font-body">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-frost-white">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-sm border-b border-ice-teal-light shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2">
                <img src="/icepop.png" alt="Waddle & Sip" className="h-8 w-8" />
                <span className="text-xl font-title font-bold text-deep-navy">WADDLE & SIP</span>
              </Link>
              <span className="text-ice-teal-dark">|</span>
              <span className="text-gray-600 font-body">CHILL SETTER DASHBOARD</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700 font-body">Welcome, {chillSetterData?.username}</span>
              <button
                onClick={handleLogout}
                className="bg-brand-red hover:bg-brand-red-light text-white font-bold px-4 py-2 rounded-lg transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-ice-teal-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { key: 'dashboard', label: 'DASHBOARD' },
              { key: 'cool-kids', label: 'MY COOL KIDS' },
              { key: 'tools', label: 'CURATOR TOOLS' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-2 border-b-2 font-bold transition-colors ${
                  activeTab === tab.key
                    ? 'border-brand-blue text-brand-blue'
                    : 'border-transparent text-gray-500 hover:text-deep-navy hover:border-ice-teal'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message && (
          <div className={`rounded-xl p-4 mb-6 ${
            message.type === 'success'
              ? 'bg-ice-teal/20 border border-ice-teal text-brand-blue'
              : 'bg-red-50 border border-red-200 text-brand-red'
          }`}>
            {message.text}
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-title font-bold text-deep-navy mb-2">YOUR CULTURAL IMPACT</h2>
              <p className="text-xl text-brand-blue font-body">"Cool isn't bought. It's shared."</p>
              <p className="text-gray-600 font-body mt-2">Track your discovery sparking journey</p>
            </div>

            {/* Statistics Grid */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Cool Kids Recruited</h3>
                <div className="text-3xl font-bold text-brand-blue">{stats.totalReferrals}</div>
                <p className="text-gray-600 text-sm">Total Discoveries: {stats.activeReferrals}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Pending Discoveries</h3>
                <div className="text-3xl font-bold text-brand-blue">{stats.pendingReferrals}</div>
                <p className="text-gray-600 text-sm">Awaiting First Sip</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Cultural Impact</h3>
                <div className="text-3xl font-bold text-brand-blue">{stats.completedReferrals}</div>
                <p className="text-gray-600 text-sm">First Sips Claimed</p>
              </div>
            </div>

            {/* Your Tools */}
            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Your Curator Tools</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-frost-white rounded-xl p-4">
                  <h4 className="text-brand-blue font-bold mb-2">Secret Code</h4>
                  <div className="flex items-center space-x-2">
                    <span className="font-mono text-deep-navy">{chillSetterData?.affiliateCode}</span>
                    <button
                      onClick={() => copyToClipboard(chillSetterData?.affiliateCode || '')}
                      className="px-2 py-1 bg-brand-blue hover:bg-brand-blue-dark text-white rounded text-xs font-bold transition-colors"
                      title="Copy secret code"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <div className="bg-frost-white rounded-xl p-4">
                  <h4 className="text-brand-blue font-bold mb-2">Magic Link</h4>
                  <div className="flex items-center space-x-2">
                    <span className="font-mono text-sm text-gray-600 truncate flex-1">
                      {chillSetterData?.referralLink}
                    </span>
                    <button
                      onClick={() => copyToClipboard(chillSetterData?.referralLink || '')}
                      className="px-2 py-1 bg-brand-blue-light hover:bg-brand-blue text-white rounded text-xs font-bold transition-colors"
                      title="Copy magic link"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cool Kids Tab */}
        {activeTab === 'cool-kids' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-title font-bold text-deep-navy mb-2">MY COOL KIDS</h2>
              <p className="text-xl text-brand-blue font-body">The cultural co-signers you've discovered</p>
              <p className="text-gray-600 font-body mt-2">Track your discovery sparking journey</p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-gray-800">
                  <thead>
                    <tr className="border-b border-ice-teal-light">
                      <th className="text-left py-3 px-4 font-bold text-deep-navy">Cool Kid</th>
                      <th className="text-left py-3 px-4 font-bold text-deep-navy">Contact</th>
                      <th className="text-left py-3 px-4 font-bold text-deep-navy">Treasure Chest</th>
                      <th className="text-left py-3 px-4 font-bold text-deep-navy">Status</th>
                      <th className="text-left py-3 px-4 font-bold text-deep-navy">Cultural Impact</th>
                      <th className="text-left py-3 px-4 font-bold text-deep-navy">Discovery Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coolKids.map((coolKid) => (
                      <tr key={coolKid._id} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                        <td className="py-3 px-4">{coolKid.userData.name}</td>
                        <td className="py-3 px-4">{coolKid.userData.email}</td>
                        <td className="py-3 px-4 font-mono text-sm">
                          {coolKid.walletDisplay || `${coolKid.userData.walletAddress?.slice(0, 6)}...${coolKid.userData.walletAddress?.slice(-4)}`}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            coolKid.status === 'completed' ? 'bg-ice-teal/30 text-brand-blue' :
                            coolKid.status === 'pending' ? 'bg-blue-100 text-brand-blue-light' :
                            'bg-red-100 text-brand-red'
                          }`}>
                            {coolKid.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">{coolKid.commission > 0 ? `${coolKid.commission} WADDLE` : 'Pending'}</td>
                        <td className="py-3 px-4">{new Date(coolKid.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Curator Tools Tab */}
        {activeTab === 'tools' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-title font-bold text-deep-navy mb-2">CURATOR TOOLS</h2>
              <p className="text-xl text-brand-blue font-body">Everything you need to spark discovery</p>
              <p className="text-gray-600 font-body mt-2">Share your magic link and watch the cool kids join</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Your Magic Link</h3>
                <div className="bg-frost-white rounded-xl p-4 mb-4">
                  <p className="text-gray-600 text-sm mb-2">Share this link to recruit Cool Kids:</p>
                  <div className="flex items-center space-x-2">
                    <span className="font-mono text-sm text-gray-800 break-all">
                      {chillSetterData?.referralLink}
                    </span>
                    <button
                      onClick={() => copyToClipboard(chillSetterData?.referralLink || '')}
                      className="px-3 py-2 bg-brand-blue hover:bg-brand-blue-dark text-white rounded font-bold transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <p className="text-gray-500 text-sm">
                  When someone uses your link, they'll join THE COOL CLUB through your curation!
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Your Secret Code</h3>
                <div className="bg-frost-white rounded-xl p-4 mb-4">
                  <p className="text-gray-600 text-sm mb-2">Your unique curator identifier:</p>
                  <div className="flex items-center space-x-2">
                    <span className="font-mono text-lg text-brand-blue font-bold">
                      {chillSetterData?.affiliateCode}
                    </span>
                    <button
                      onClick={() => copyToClipboard(chillSetterData?.affiliateCode || '')}
                      className="px-3 py-2 bg-brand-blue-light hover:bg-brand-blue text-white rounded font-bold transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <p className="text-gray-500 text-sm">
                  This code identifies you as the cultural curator who sparked their discovery.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
              <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Curator Tips</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-brand-blue font-bold mb-2">How to Spark Discovery</h4>
                  <ul className="text-gray-600 space-y-2">
                    <li>• Share your magic link on social media</li>
                    <li>• Tell friends about THE COOL CLUB</li>
                    <li>• Create content about Waddle & Sip</li>
                    <li>• Host events and share your link</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-brand-blue font-bold mb-2">Cultural Curator Best Practices</h4>
                  <ul className="text-gray-600 space-y-2">
                    <li>• Be authentic in your recommendations</li>
                    <li>• Focus on quality over quantity</li>
                    <li>• Engage with your Cool Kids</li>
                    <li>• Stay true to your personal brand</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChillSetterDashboard;
